import { z } from 'zod'

export const VServerStatus = z.enum([
  'provisioning',
  'active',
  'stopped',
  'failed',
  'resizing',
  'reinstalling',
  'suspended',
  'deprovisioning',
  'deprovisioned'
])
export type VServerStatus = z.infer<typeof VServerStatus>

// Guest-agent liveness, reported only by the single GET /vserver/{id} endpoint.
// "stopped" means the customer uninstalled/stopped the agent; null = unknown (VM off).
export const VServerGuestAgent = z.enum(['running', 'stopped'])
export type VServerGuestAgent = z.infer<typeof VServerGuestAgent>

export const VServerInfo = z.object({
  id: z.number(),
  hostname: z.string(),
  status: VServerStatus,
  error_message: z.string().nullable().optional(),
  proxmox_vmid: z.number().nullable().optional(),
  proxmox_node: z.string().nullable().optional(),
  vcpu: z.number(),
  memory_mb: z.number(),
  disk_gb: z.number(),
  template_slug: z.string(),
  plan_slug: z.string(),
  ipv4_address: z.string().nullable().optional(),
  ipv6_address: z.string().nullable().optional(),
  stripe_subscription_id: z.string().nullable().optional(),
  // Provisioned without an SSH key and none injected since — show the
  // "add an access key" nudge.
  provisioned_keyless: z.boolean().optional(),
  provisioned_at: z.number().nullable().optional(),
  created_at: z.number(),
  // When a suspended server (its subscription ended) is deleted, Unix seconds.
  deletion_at: z.number().nullable().optional(),
  live_status: z.string().nullable().optional(),
  uptime_seconds: z.number().nullable().optional(),
  guest_agent: VServerGuestAgent.nullable().optional(),
  // Whether this deployment can run rescue mode / set PTR records at all.
  rescue_available: z.boolean().optional(),
  reverse_dns_available: z.boolean().optional(),
  // Customer snapshots the plan includes; automatic safety snapshots do not count.
  snapshot_limit: z.number().optional(),
  // Locked by an operator (abuse): stopped, and start, rebuild, console and
  // firewall changes are refused with 409 vserver_locked. Deleting stays allowed.
  locked: z.boolean().optional(),
  lock_note: z.string().nullable().optional(),
  locked_at: z.number().nullable().optional()
})
export type VServerInfo = z.infer<typeof VServerInfo>
